import { Observable, of } from 'rxjs'
import { filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import {
  ClientEventAction,
  EntityType,
  EntityTypeEnum,
  EntityWrapper,
  ReferenceDataManagementDataUpdate,
  WorkflowActions,
  WorkflowActionsEnum
} from 'api'
import { GlobalState } from 'store'
import { NotificationActions } from 'store/notification'
import { ofEntityUpdateType } from './epicUtils'
import { penultimate } from 'utils'
import { EntityTypeIdRecord, PendingWorkflowTypeEnum } from 'store/notification/notificationTypes'
import { workflowReferenceDataEntitiesGetterSelector } from 'selectors/workflowSelectors'
import { existingTradingEntityMetaGetterSelector } from 'selectors/tradingEntitySelectors'
import { tradingEntityClearingsByTradingEntityIdSelector } from 'selectors/clearingSelectors'
import { createPrimeBrokersByOrganisationGetterSelector } from 'selectors/organisationSelectors'
import { createOrganisationMetasByOrganisationGetterSelector } from 'selectors/organisationSelectors'
import { tradingRelationshipsGetterSelector } from 'selectors/tradingRelationshipSelectors'
import { BTQAction } from 'epics'
import { fatFingerLimitUserGetterSelector } from 'selectors/userSelectors'

export type CreateNewWorkflowAction = ReturnType<typeof WorkflowActions.createNewWorkflow>
export type CreateEditWorkflowAction = ReturnType<typeof WorkflowActions.createEditWorkflow>
export type CreateCloneWorkflowAction = ReturnType<typeof WorkflowActions.createCloneWorkflow>
export type AddToExistingWorkflowAction = ReturnType<typeof WorkflowActions.createAddToExistingWorkflow>

export function workflowCreateRequestEpic(action$: Observable<BTQAction>): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WorkflowActionsEnum.CREATE_NEW_WORKFLOW, CreateNewWorkflowAction>(WorkflowActionsEnum.CREATE_NEW_WORKFLOW),
    mergeMap(action => {
      const { entityTypeEnum, correlatedResponseCallback } = action.payload

      let entityTypeEnums

      switch (entityTypeEnum) {
        case EntityType.COLLATERAL:
          entityTypeEnums = [EntityTypeEnum.COLLATERAL, EntityTypeEnum.COLLATERAL_PRICE]
          break
        default:
          entityTypeEnums = [entityTypeEnum]
      }

      const createReferenceDataAction = WorkflowActions.createNewReferenceData(entityTypeEnums)

      return of(
        NotificationActions.createPendingWorkflow(createReferenceDataAction.payload.correlationId, {
          workflowType: PendingWorkflowTypeEnum.NEW_WORKFLOW,
          correlatedResponseCallback
        }),
        createReferenceDataAction
      )
    })
  )
}

const createCloneRefereceDataAction = (
  _state: GlobalState,
  entityType: EntityTypeEnum,
  entityId: string,
  _entityTypeOnly: boolean // Edits only the entity type given, not any related entity types
) => {
  switch (entityType) {
    case EntityTypeEnum.HAIRCUT_ENTRY:
      return WorkflowActions.createCloneReferenceData([
        {
          entityType,
          entityId
        }
      ])
    default:
      throw new Error(`Unsupported entity type in workflow clone request`)
  }
}

const createEditReferenceDataAction = (
  state: GlobalState,
  entityType: EntityTypeEnum,
  entityId: string,
  entityTypeOnly: boolean // Edits only the entity type given, not any related entity types
) => {
  switch (entityType) {
    case EntityTypeEnum.TRADING_ENTITY:
      const tradingRelationships = entityTypeOnly ? [] : tradingRelationshipsGetterSelector(state)(entityId)
      const tradingEntityMetas = entityTypeOnly ? [] : existingTradingEntityMetaGetterSelector(state)(entityId)
      const tradingEntityClearings = entityTypeOnly ? [] : tradingEntityClearingsByTradingEntityIdSelector(state)(entityId)

      return WorkflowActions.createEditReferenceData([
        {
          entityType: EntityTypeEnum.TRADING_ENTITY,
          entityId
        },
        ...tradingRelationships.map(tradingRelationship => ({
          entityType: EntityTypeEnum.TRADING_RELATIONSHIP,
          entityId: tradingRelationship.id
        })),
        ...tradingEntityMetas.map(tradingEntityMeta => ({
          entityType: EntityTypeEnum.TRADING_ENTITY_META,
          entityId: tradingEntityMeta.id
        })),
        ...tradingEntityClearings.map(tradingEntityClearing => ({
          entityType: EntityTypeEnum.TRADING_ENTITY_CLEARING,
          entityId: tradingEntityClearing.id
        }))
      ])

    case EntityTypeEnum.COLLATERAL:
      return WorkflowActions.createEditReferenceData([
        {
          entityType: EntityTypeEnum.COLLATERAL,
          entityId: entityId
        },
        {
          entityType: EntityTypeEnum.COLLATERAL_PRICE,
          entityId: entityId
        }
      ])
    case EntityTypeEnum.ORGANISATION:
      const primeBrokers = createPrimeBrokersByOrganisationGetterSelector(state)(entityId)
      const organisationMetas = createOrganisationMetasByOrganisationGetterSelector(state)(entityId)
      return WorkflowActions.createEditReferenceData([
        {
          entityType: EntityTypeEnum.ORGANISATION,
          entityId
        },
        ...primeBrokers.map(primeBroker => ({
          entityType: EntityTypeEnum.PRIME_BROKER,
          entityId: primeBroker.id
        })),
        ...organisationMetas.map(organisationMeta => ({
          entityType: EntityTypeEnum.ORGANISATION_META,
          entityId: organisationMeta.id
        }))
      ])
    case EntityTypeEnum.USER:
      const fingerLimits = fatFingerLimitUserGetterSelector(state)(entityId).map(element => element.fatFingerUserLimit)
      return WorkflowActions.createEditReferenceData([
        {
          entityType: EntityTypeEnum.USER,
          entityId
        },
        ...fingerLimits.map(f => ({
          entityType: EntityTypeEnum.FAT_FINGER_USER,
          entityId: f.id
        }))
      ])
    case EntityTypeEnum.USER_PREFERENCE:
    case EntityTypeEnum.CLEARING_HOUSE:
    case EntityTypeEnum.COLLATERAL_BASKET:
    case EntityTypeEnum.INDEX:
    case EntityTypeEnum.HAIRCUT_ENTRY:
    case EntityTypeEnum.TRADING_CONTROL_LIMIT:
    case EntityTypeEnum.PRODUCT:
    case EntityTypeEnum.PRODUCT_SECTOR:
    case EntityTypeEnum.FX_RATE:
    case EntityTypeEnum.TRADING_RELATIONSHIP:
    case EntityTypeEnum.TRADING_ENTITY_META:
    case EntityTypeEnum.ISSUER_ORGANISATION:
    case EntityTypeEnum.PRODUCT_SECTOR_MAPPING_DRAFT:
    case EntityTypeEnum.PRODUCT_SECTOR_MAPPING_STATE:
    case EntityTypeEnum.FAT_FINGER_USER:
      return WorkflowActions.createEditReferenceData([
        {
          entityType,
          entityId
        }
      ])

    default:
      throw new Error(`Unsupported entity type in workflow edit request`)
  }
}

export function workflowEditRequestEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WorkflowActionsEnum.CREATE_EDIT_WORKFLOW, CreateEditWorkflowAction>(WorkflowActionsEnum.CREATE_EDIT_WORKFLOW),
    mergeMap(action => {
      const { entityTypeEnum, entityId, correlatedResponseCallback, entityTypeOnly } = action.payload
      const createAction: ClientEventAction = createEditReferenceDataAction(state$.value, entityTypeEnum, entityId, entityTypeOnly)

      return of(
        NotificationActions.createPendingWorkflow(createAction.payload.correlationId, {
          workflowType: PendingWorkflowTypeEnum.EDIT_WORKFLOW,
          correlatedResponseCallback
        }),
        createAction
      )
    })
  )
}

export function workflowCloneRequestEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WorkflowActionsEnum.CREATE_CLONE_WORKFLOW, CreateCloneWorkflowAction>(WorkflowActionsEnum.CREATE_CLONE_WORKFLOW),
    mergeMap(action => {
      const { entityTypeEnum, entityId, correlatedResponseCallback, entityTypeOnly } = action.payload
      const createAction: ClientEventAction = createCloneRefereceDataAction(state$.value, entityTypeEnum, entityId, entityTypeOnly)

      return of(
        NotificationActions.createPendingWorkflow(createAction.payload.correlationId, {
          workflowType: PendingWorkflowTypeEnum.CLONE_WORKFLOW,
          correlatedResponseCallback
        }),
        createAction
      )
    })
  )
}

export function workflowAddToExistingRequestEpic(action$: Observable<BTQAction>): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, WorkflowActionsEnum.ADD_TO_EXISTING_WORKFLOW, AddToExistingWorkflowAction>(
      WorkflowActionsEnum.ADD_TO_EXISTING_WORKFLOW
    ),
    mergeMap(action => {
      const { entityTypeEnum, correlatedResponseCallback, workflowId } = action.payload
      const createReferenceDataAction = WorkflowActions.createEntityAddReferenceData([entityTypeEnum], workflowId)

      return of(
        NotificationActions.createPendingWorkflow(createReferenceDataAction.payload.correlationId, {
          workflowType: PendingWorkflowTypeEnum.ADD_ENTITY,
          correlatedResponseCallback
        }),
        createReferenceDataAction
      )
    })
  )
}

const toEntityTypeIdRecord = (referenceDataEntities: EntityWrapper[]): EntityTypeIdRecord => {
  return referenceDataEntities.reduce((acc, referenceDataEntity) => {
    if (referenceDataEntity.entityTypeEnum && referenceDataEntity.id) {
      acc[referenceDataEntity.entityTypeEnum] = referenceDataEntity.id
    }
    return acc
  }, {} as EntityTypeIdRecord)
}

export function getNewlyAddedEntityId(action: ReferenceDataManagementDataUpdate, state: GlobalState): EntityTypeIdRecord | null {
  const { referenceDataEntities, workflowId } = action.entity

  if (referenceDataEntities.length === 0) {
    return null
  }

  if (referenceDataEntities.length === 1) {
    return toEntityTypeIdRecord(referenceDataEntities)
  }

  const getExistingEntitiesByWorkflow = workflowReferenceDataEntitiesGetterSelector(state)
  const existingEntities = getExistingEntitiesByWorkflow(workflowId)

  const newEntities = referenceDataEntities.filter(newEntity => {
    return !existingEntities.some(existingEntity => existingEntity.id === newEntity.id)
  })

  return toEntityTypeIdRecord(newEntities)
}

export function workflowResponseEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  return action$.pipe(
    ofEntityUpdateType(EntityType.REFERENCE_DATA_MANAGEMENT_DATA),
    withLatestFrom(state$, state$.pipe(penultimate())),
    filter(([action, currentState]) => {
      return !!currentState.notificationStore.pendingWorkflows[action.lastCorrelationId]
    }),
    tap(([action, currentState, previousState]) => {
      const pendingWorkflow = currentState.notificationStore.pendingWorkflows[action.lastCorrelationId]

      const { correlatedResponseCallback, workflowType } = pendingWorkflow

      const addedEntityIds =
        workflowType === PendingWorkflowTypeEnum.ADD_ENTITY ||
        workflowType === PendingWorkflowTypeEnum.NEW_WORKFLOW ||
        workflowType === PendingWorkflowTypeEnum.CLONE_WORKFLOW
          ? getNewlyAddedEntityId(action, previousState)
          : null

      if (correlatedResponseCallback) {
        correlatedResponseCallback(action.entity.workflowId, addedEntityIds)
      }
    }),
    map(([action]) => {
      return NotificationActions.removePendingWorkflow(action.lastCorrelationId)
    })
  )
}

export const workflowEpic = combineEpics(
  workflowCreateRequestEpic,
  workflowEditRequestEpic,
  workflowCloneRequestEpic,
  workflowAddToExistingRequestEpic,
  workflowResponseEpic
)
